import React, { useState, useEffect } from 'react';
import { AdminService } from '../../services/dataServices';
import {
  Users,
  Shield,
  ShieldOff,
  UserCheck,
  UserX,
  CheckCircle,
  Award,
  Clock,
  Crown,
  GraduationCap,
  UserCog,
  AlertCircle,
  Search,
  Mail,
  Calendar,
  XCircle,
  Trash2
} from 'lucide-react';
import AttendanceDashboard from './AttendanceDashboard';

const AdminUserManagement: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'admin' | 'student' | 'no_mentor' | 'inactive' | 'dropout' | 'placed'>('all');
  const [updating, setUpdating] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showStatusModal, setShowStatusModal] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<'active' | 'inactive' | 'dropout' | 'placed' | 'on_leave' | 'kitchen_leave' | 'unapproved_leave'>('active');
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'academic_associate' | 'super_mentor' | 'mentor' | 'student'>('student');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await AdminService.getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId: string, currentStatus: boolean) => {
    try {
      setUpdating(userId);
      await AdminService.updateUserAdminStatus(userId, !currentStatus);

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, isAdmin: !currentStatus } : user
      ));
    } catch (error) {
      console.error('Error updating admin status:', error);
      alert('Failed to update admin status');
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setUpdating(userId);
      await AdminService.deleteUser(userId);

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, status: 'inactive' } : user
      ));

      setShowDeleteModal(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateStatus = async (userId: string, newStatus: 'active' | 'inactive' | 'dropout' | 'placed' | 'on_leave' | 'kitchen_leave' | 'unapproved_leave') => {
    try {
      setUpdating(userId);
      await AdminService.updateUserStatus(userId, newStatus);

      // Update local state with the new status and unapproved_leave_start if applicable
      setUsers(users.map(user => {
        if (user.id === userId) {
          const updatedUser = { ...user, status: newStatus };
          if (newStatus === 'unapproved_leave') {
            updatedUser.unapproved_leave_start = new Date();
          } else {
            updatedUser.unapproved_leave_start = undefined;
          }
          return updatedUser;
        }
        return user;
      }));

      setShowStatusModal(null);
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'academic_associate' | 'super_mentor' | 'mentor' | 'student') => {
    try {
      setUpdating(userId);
      await AdminService.updateUserRole(userId, newRole);

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, role: newRole } : user
      ));

      setShowRoleModal(false);
      setSelectedUser(null);
      setSelectedRole('student');
      setSuccessMessage('User role updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating user role:', error);
      setErrorMessage('Failed to update user role');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setUpdating(null);
    }
  };

  const handleOpenRoleModal = (user: any) => {
    setSelectedUser(user);
    setSelectedRole(user.role);
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setSelectedUser(null);
    setSelectedRole('student');
    setShowRoleModal(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'inactive': return <XCircle className="h-4 w-4 text-gray-500" />;
      case 'dropout': return <UserX className="h-4 w-4 text-red-500" />;
      case 'placed': return <Award className="h-4 w-4 text-purple-500" />;
      case 'on_leave': return <Clock className="h-4 w-4 text-orange-500" />;
      case 'kitchen_leave': return <Clock className="h-4 w-4 text-blue-500" />;
      case 'unapproved_leave': return <Clock className="h-4 w-4 text-red-600" />;
      default: return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Active';
      case 'inactive': return 'Inactive';
      case 'dropout': return 'Dropout';
      case 'placed': return 'Placed';
      case 'on_leave': return 'On Leave';
      case 'kitchen_leave': return 'Kitchen Leave';
      case 'unapproved_leave': return 'Unapproved Leave';
      default: return 'Active';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'dropout': return 'bg-red-100 text-red-800';
      case 'placed': return 'bg-purple-100 text-purple-800';
      case 'on_leave': return 'bg-orange-100 text-orange-800';
      case 'kitchen_leave': return 'bg-blue-100 text-blue-800';
      case 'unapproved_leave': return 'bg-red-100 text-red-900';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Crown className="h-3 w-3 mr-1" />;
      case 'academic_associate': return <GraduationCap className="h-3 w-3 mr-1" />;
      case 'super_mentor': return <UserCog className="h-3 w-3 mr-1" />;
      case 'mentor': return <UserCheck className="h-3 w-3 mr-1" />;
      case 'student': return <Users className="h-3 w-3 mr-1" />;
      default: return <Users className="h-3 w-3 mr-1" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'academic_associate': return 'Academic Associate';
      case 'super_mentor': return 'Super Mentor';
      case 'mentor': return 'Mentor';
      case 'student': return 'Student';
      default: return 'Student';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'academic_associate': return 'bg-blue-100 text-blue-800';
      case 'super_mentor': return 'bg-purple-100 text-purple-800';
      case 'mentor': return 'bg-green-100 text-green-800';
      case 'student': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin': return 'Full system access, user management, backend operations';
      case 'academic_associate': return 'Admin access without backend management';
      case 'super_mentor': return 'Mentor management and advanced mentoring';
      case 'mentor': return 'Student mentoring and feedback';
      case 'student': return 'Learning access, goal setting, reflections';
      default: return 'Standard student access';
    }
  };

  const filteredUsers = users.filter(user => {
    // Text search
    const matchesSearch = !searchTerm ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Type filter
    const matchesFilter = filterType === 'all' ||
      (filterType === 'admin' && user.isAdmin) ||
      (filterType === 'student' && !user.isAdmin) ||
      (filterType === 'no_mentor' && !user.isAdmin && !user.mentor_id) ||
      (filterType === 'inactive' && user.status === 'inactive') ||
      (filterType === 'dropout' && user.status === 'dropout') ||
      (filterType === 'placed' && user.status === 'placed');

    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: users.length,
    admins: users.filter(u => u.isAdmin).length,
    students: users.filter(u => !u.isAdmin).length,
    withoutMentor: users.filter(u => !u.isAdmin && !u.mentor_id).length,
    active: users.filter(u => !u.status || u.status === 'active').length,
    inactive: users.filter(u => u.status === 'inactive').length,
    dropout: users.filter(u => u.status === 'dropout').length,
    placed: users.filter(u => u.status === 'placed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Admins</p>
              <p className="text-2xl font-bold text-gray-900">{stats.admins}</p>
            </div>
            <Shield className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.students}</p>
            </div>
            <UserCheck className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">No Mentor</p>
              <p className="text-2xl font-bold text-gray-900">{stats.withoutMentor}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Placed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.placed}</p>
            </div>
            <Award className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Dropout</p>
              <p className="text-2xl font-bold text-gray-900">{stats.dropout}</p>
            </div>
            <UserX className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Attendance Dashboard */}
      <AttendanceDashboard />

      {/* Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Filter Buttons */}
          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'all'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('admin')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'admin'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Admins
            </button>
            <button
              onClick={() => setFilterType('student')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'student'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Students
            </button>
            <button
              onClick={() => setFilterType('no_mentor')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'no_mentor'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              No Mentor
            </button>
            <button
              onClick={() => setFilterType('inactive')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'inactive'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Inactive
            </button>
            <button
              onClick={() => setFilterType('dropout')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'dropout'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Dropout
            </button>
            <button
              onClick={() => setFilterType('placed')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'placed'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Placed
            </button>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mentor Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-700 font-medium">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {getRoleIcon(user.role)}
                          {getRoleLabel(user.role)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status || 'active')}`}>
                        {getStatusIcon(user.status || 'active')}
                        <span className="ml-1">{getStatusLabel(user.status || 'active')}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.mentor_id ? (
                        <span className="text-green-600">Has Mentor</span>
                      ) : user.isAdmin ? (
                        <span className="text-gray-400">N/A</span>
                      ) : (
                        <span className="text-orange-600">No Mentor</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        {/* Admin Toggle Button */}
                        <button
                          onClick={() => handleToggleAdmin(user.id, user.isAdmin || false)}
                          disabled={updating === user.id}
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${user.isAdmin
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                            } ${updating === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={user.isAdmin ? 'Remove admin privileges' : 'Grant admin privileges'}
                        >
                          {updating === user.id ? (
                            <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-current"></span>
                          ) : user.isAdmin ? (
                            <ShieldOff className="h-3 w-3" />
                          ) : (
                            <Shield className="h-3 w-3" />
                          )}
                        </button>

                        {/* Role Change Button */}
                        <button
                          onClick={() => handleOpenRoleModal(user)}
                          disabled={updating === user.id}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors disabled:opacity-50"
                          title="Change Role"
                        >
                          <UserCog className="h-3 w-3" />
                        </button>

                        {/* Status Button */}
                        <button
                          onClick={() => setShowStatusModal(user.id)}
                          disabled={updating === user.id}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors disabled:opacity-50"
                          title="Change user status"
                        >
                          {getStatusIcon(user.status || 'active')}
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => setShowDeleteModal(user.id)}
                          disabled={updating === user.id}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
                          title="Delete user"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">User Management Tips:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Admins have full access to all features including user management and reports</li>
              <li>Students can access goals, reflections, and mentor dashboards</li>
              <li>Use the "Mentor Assignment" tab to assign mentors to students</li>
              <li>Students without mentors won't receive feedback on their reflections</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Delete User Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
              <button
                onClick={() => setShowDeleteModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this user? This will set their status to inactive.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => showDeleteModal && handleDeleteUser(showDeleteModal)}
                disabled={updating !== null}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {updating ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Update User Status</h3>
              <button
                onClick={() => setShowStatusModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as 'active' | 'inactive' | 'dropout' | 'placed' | 'on_leave' | 'kitchen_leave' | 'unapproved_leave')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="dropout">Dropout</option>
                <option value="placed">Placed</option>
                <option value="on_leave">On Leave</option>
                <option value="kitchen_leave">Kitchen Leave</option>
                <option value="unapproved_leave">Unapproved Leave</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowStatusModal(null)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => showStatusModal && handleUpdateStatus(showStatusModal, selectedStatus)}
                disabled={updating !== null}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <UserCog className="h-6 w-6 text-purple-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Change User Role</h3>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>User:</strong> {selectedUser.name} ({selectedUser.email})
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Current Role:</strong> {getRoleLabel(selectedUser.role)}
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Role
              </label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'academic_associate' | 'super_mentor' | 'mentor' | 'student')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="student">Student - Learning access, goal setting, reflections</option>
                <option value="mentor">Mentor - Student mentoring and feedback</option>
                <option value="super_mentor">Super Mentor - Mentor management and advanced mentoring</option>
                <option value="academic_associate">Academic Associate - Admin access without backend management</option>
                <option value="admin">Admin - Full system access, user management, backend operations</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                {getRoleDescription(selectedRole)}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseRoleModal}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => selectedUser && handleUpdateRole(selectedUser.id, selectedRole)}
                disabled={updating !== null}
                className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {updating ? 'Updating...' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
